import type { VisitOptions } from "@inertiajs/core"
import { router } from "@inertiajs/react"
import { getCsrfToken } from "@/lib/workspace-api"

export function runWorkspace(
  projectId: number,
  verb: string,
  extra: Record<string, unknown> = {},
  options: Partial<VisitOptions> = {},
) {
  router.post(
    `/projects/${projectId}/workspace/run`,
    {
      verb,
      ...extra,
      workspace: { verb, ...extra },
    },
    { preserveScroll: true, ...options },
  )
}

export type StoryboardAnimaticRefFrame = "first" | "poster" | "last"

export function patchShotVideoSettings(
  projectId: number,
  shotId: number,
  body: {
    duration_target_sec?: number
    frame_format?: string | null
    video_shot_type?: string
    seedance_adaptive_duration?: boolean
    kling_video_reference_mode?: string
    kling_video_references?: { role: string; source?: string; url?: string; label?: string; hero_label?: string }[]
  },
) {
  router.patch(`/projects/${projectId}/workspace/shots/${shotId}/video_settings`, body, {
    preserveScroll: true,
  })
}

export async function patchShotFrameFormat(
  projectId: number,
  shotId: number,
  frameFormat: string | null,
): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/video_settings`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "same-origin",
    body: JSON.stringify({ frame_format: frameFormat ?? "" }),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? "Failed to save frame format")
  }
}

async function patchShotVideoSettingField(
  projectId: number,
  shotId: number,
  body: Record<string, string | number | boolean | null>,
  errorFallback: string,
): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/${shotId}/video_settings`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    credentials: "same-origin",
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) {
    throw new Error(data.error ?? errorFallback)
  }
}

export async function patchShotVideoModelKey(
  projectId: number,
  shotId: number,
  videoModelKey: string | null,
): Promise<void> {
  await patchShotVideoSettingField(
    projectId,
    shotId,
    { video_model_key: videoModelKey ?? "" },
    "Failed to save video model",
  )
}

/** Звук в видео этого шота; null — как в проекте. */
export async function patchShotGenerateAudio(
  projectId: number,
  shotId: number,
  generateAudio: boolean | null,
): Promise<void> {
  await patchShotVideoSettingField(
    projectId,
    shotId,
    { generate_audio: generateAudio == null ? "" : generateAudio },
    "Failed to save video sound",
  )
}

/** Модель кадра и раскладки этого шота; null — модель проекта. */
export async function patchShotStoryboardModelKey(
  projectId: number,
  shotId: number,
  storyboardModelKey: string | null,
): Promise<void> {
  await patchShotVideoSettingField(
    projectId,
    shotId,
    { storyboard_model_key: storyboardModelKey ?? "" },
    "Failed to save frame model",
  )
}

export async function patchShotVideoResolution(
  projectId: number,
  shotId: number,
  videoResolution: string | null,
): Promise<void> {
  await patchShotVideoSettingField(
    projectId,
    shotId,
    { video_resolution: videoResolution ?? "" },
    "Failed to save video resolution",
  )
}

export async function patchShotVideoDraft(
  projectId: number,
  shotId: number,
  videoDraft: boolean | null,
): Promise<void> {
  await patchShotVideoSettingField(
    projectId,
    shotId,
    { video_draft: videoDraft == null ? "" : videoDraft },
    "Failed to save draft mode",
  )
}

export async function postShotDraftFinalize(
  projectId: number,
  shotId: number,
  versionId: number,
  resolution: string,
): Promise<void> {
  const csrf = getCsrfToken()
  const res = await fetch(
    `/projects/${projectId}/workspace/shots/${shotId}/media_versions/${versionId}/finalize_draft`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrf,
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify({ resolution }),
    },
  )
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string }
  if (!res.ok || !data.ok) throw new Error(data.error ?? "Failed to finalize draft")
}

export function postRestoreVersionSnapshot(projectId: number, snapshotId: number, visitOptions?: VisitOptions) {
  router.post(
    `/projects/${projectId}/workspace/version_snapshots/${snapshotId}/restore`,
    {},
    { preserveScroll: true, ...visitOptions },
  )
}

export type ShotCharacterAssignment = {
  character_id: number
  outfit_id: number | null
  include_description_in_prompt?: boolean
}

export function patchShotCharacters(
  projectId: number,
  shotId: number,
  body: {
    character_assignments: ShotCharacterAssignment[]
    storyboard_no_people: boolean
  },
  visitOptions?: VisitOptions,
) {
  router.patch(`/projects/${projectId}/workspace/shots/${shotId}/shot_characters`, body, {
    preserveScroll: true,
    ...visitOptions,
  })
}

export type ShotPropAssignment = { prop_id: number; state_id: number | null }

export function patchShotProps(
  projectId: number,
  shotId: number,
  body: {
    prop_assignments: ShotPropAssignment[]
  },
  visitOptions?: VisitOptions,
) {
  router.patch(`/projects/${projectId}/workspace/shots/${shotId}/shot_props`, body, {
    preserveScroll: true,
    ...visitOptions,
  })
}

export function patchShotLocationVariant(
  projectId: number,
  shotId: number,
  body: {
    location_variant_id: number | null
    location_facing?: string
    location_viewpoint_id?: number | null
  },
  visitOptions?: VisitOptions,
) {
  router.patch(`/projects/${projectId}/workspace/shots/${shotId}/location_variant`, body, {
    preserveScroll: true,
    ...visitOptions,
  })
}

export function createShot(
  projectId: number,
  body: {
    scene_id?: number
    insert_after_position: number
    intent_summary: string
    duration_target_sec?: number
    location_variant_id?: number | null
    character_ids?: number[]
    use_neighbor_last_frame?: boolean
    shot_kind?: "regular" | "transition"
    task_id?: number | null
  },
  visitOptions?: VisitOptions,
) {
  router.post(`/projects/${projectId}/workspace/shots/create`, body, {
    preserveScroll: true,
    ...visitOptions,
  })
}

export async function createShotJson(
  projectId: number,
  body: {
    scene_id?: number
    insert_after_position: number
    intent_summary: string
    duration_target_sec?: number
    location_variant_id?: number | null
    character_ids?: number[]
    use_neighbor_last_frame?: boolean
    shot_kind?: "regular" | "transition"
    task_id?: number | null
  },
): Promise<{ ok: boolean; shot_id?: number; error?: string }> {
  const csrf = getCsrfToken()
  const res = await fetch(`/projects/${projectId}/workspace/shots/create`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as { ok?: boolean; shot_id?: number; error?: string }
  if (!res.ok || !data.ok) {
    return { ok: false, error: data.error ?? `HTTP ${res.status}` }
  }
  return { ok: true, shot_id: data.shot_id }
}

export function toggleShotVisibility(projectId: number, shotId: number) {
  router.patch(
    `/projects/${projectId}/workspace/shots/${shotId}/toggle_visibility`,
    {},
    {
      preserveScroll: true,
    },
  )
}

export function destroyShot(projectId: number, shotId: number, visitOptions?: VisitOptions) {
  router.delete(`/projects/${projectId}/workspace/shots/${shotId}`, {
    preserveScroll: true,
    ...visitOptions,
  })
}

export function extractVideoFrames(projectId: number, shotId: number, visitOptions?: VisitOptions) {
  router.post(
    `/projects/${projectId}/workspace/shots/${shotId}/extract_frames`,
    {},
    {
      preserveScroll: true,
      ...visitOptions,
    },
  )
}
